import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import Info from '@components/common/info';
import { Icon } from '@components/icons';
import { copyStringToClipboard, formatDate } from '@utils/index';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiKeyService, describeScopes } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import type { Environment } from '@context/environment_context';
import Skeleton from '@components/common/skeleton';
import { useSettingsModals } from '@hooks/modals/useSettingsModals';
import DevPageHeader from '../_shared/PageHeader';

const DevelopersApiKeysLayout = () => {
    const { user } = useUserContext();
    const { handleCreateApiKey, handleDeleteApiKey } = useSettingsModals();
    // Sandbox and live keys are isolated server-side; the toggle scopes every
    // read/create/delete to one environment. env/setEnv come from the shared
    // EnvironmentContext (dev-portal enterprise redesign spec §3.2 Option B)
    // so this page and the topbar pill stay in sync — default is Sandbox,
    // set once at EnvironmentProvider (safe default).
    const { env, setEnv } = useEnvironmentContext();

    const {
        data: apiKeys,
        isLoading: isApiKeysLoading,
        handleRequest: refetchApiKeys
    } = useApiQuery({
        method: () => apiKeyService.get(env),
        deps: [user, env],
        condition: !!user
    });

    const isTestKey = env === 'test';
    const hasKey = !!apiKeys?.access_key;

    const permLevel = describeScopes(apiKeys?.scopes);
    const permHint =
        permLevel === 'Read only'
            ? 'Read-only access'
            : permLevel === 'Custom'
            ? 'Custom scopes'
            : 'Read & write access';

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <DevPageHeader
                title="API keys"
                subtitle="Use your secret key to authenticate API requests and validate webhook signatures."
                actions={
                    hasKey ? (
                        <Button
                            variant="outlined-red"
                            size="small"
                            leftIcon="trash"
                            onClick={() => handleDeleteApiKey(refetchApiKeys, env)}>
                            Delete key
                        </Button>
                    ) : undefined
                }
            />

            <div className={styles.env_toggle} role="tablist" aria-label="Key environment">
                {(['test', 'live'] as Environment[]).map((e) => (
                    <button
                        key={e}
                        type="button"
                        role="tab"
                        aria-selected={env === e}
                        className={[
                            styles.env_toggle_btn,
                            env === e ? styles.env_toggle_active : ''
                        ].join(' ')}
                        onClick={() => setEnv(e)}>
                        {e === 'test' ? 'Sandbox' : 'Live'}
                    </button>
                ))}
            </div>

            {isApiKeysLoading ? (
                <div className={styles.card}>
                    <Skeleton className={styles.skeleton} />
                </div>
            ) : (
                <div className={styles.card}>
                    <div className={styles.card_header}>
                        <div className={styles.left}>
                            <div className={styles.icon_circle}>
                                <Icon name="key" width={22} height={22} />
                            </div>
                            <div className={styles.text_wrapper}>
                                <Typography variant="body2" weight="semibold">
                                    {isTestKey ? 'Sandbox' : 'Live'} REST API key
                                </Typography>
                                <p className={styles.description}>
                                    Keep your secret key confidential — treat it like a password.
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="gold"
                            size="small"
                            leftIcon={hasKey ? 'refresh' : 'plus'}
                            onClick={() =>
                                handleCreateApiKey(refetchApiKeys, apiKeys || undefined, env)
                            }>
                            {hasKey ? 'Regenerate key' : 'Create key'}
                        </Button>
                    </div>

                    {hasKey ? (
                        <div className={styles.content}>
                            <div className={styles.field}>
                                <div className={styles.field_top}>
                                    <p className={styles.label}>Access key</p>
                                    <div
                                        className={[
                                            styles.env_pill,
                                            isTestKey ? styles.env_test : styles.env_live
                                        ].join(' ')}>
                                        <span className={styles.env_dot} />
                                        <span className={styles.env_label}>
                                            {isTestKey ? 'Sandbox' : 'Live'}
                                        </span>
                                    </div>
                                </div>
                                <div className={[styles.key_row, styles.key_row_dark].join(' ')}>
                                    <span
                                        className={[styles.key_value, styles.key_value_dark].join(
                                            ' '
                                        )}>
                                        {apiKeys?.access_key}
                                    </span>
                                    <button
                                        className={styles.copy_btn_dark}
                                        onClick={() =>
                                            copyStringToClipboard(apiKeys?.access_key || '')
                                        }>
                                        <Icon name="copy" width={16} height={16} />
                                        Copy
                                    </button>
                                </div>
                            </div>

                            <div className={styles.field}>
                                <p className={styles.label}>Secret key</p>
                                <div className={[styles.key_row, styles.key_row_dark].join(' ')}>
                                    <span
                                        className={[styles.key_value, styles.key_value_dark].join(
                                            ' '
                                        )}>
                                        ••••••••••••••••••••••••••••••••
                                    </span>
                                    <span
                                        className={[
                                            styles.secret_note,
                                            styles.secret_note_dark
                                        ].join(' ')}>
                                        <Icon name="lock" width={13} height={13} />
                                        Shown once at creation
                                    </span>
                                </div>
                            </div>

                            <div className={styles.field}>
                                <div className={styles.label_row}>
                                    <p className={styles.label}>Permissions</p>
                                    <Info
                                        variant="right"
                                        content={
                                            <>
                                                <strong>Full access</strong> can create
                                                charges, send payouts, and issue refunds.{' '}
                                                <strong>Read-only</strong> can only view
                                                data — it can never write, refund, or
                                                change settings.
                                            </>
                                        }
                                    />
                                </div>
                                <div className={styles.key_row}>
                                    <span className={styles.perm_value}>{permLevel}</span>
                                    <span className={styles.secret_note}>{permHint}</span>
                                </div>
                            </div>

                            <div className={styles.meta}>
                                <Icon name="clock" width={14} height={14} />
                                Created {formatDate(apiKeys?.created_at, 'MMM D, YYYY')}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.empty_state}>
                            <div className={styles.empty_icon}>
                                <Icon name="key" width={26} height={26} />
                            </div>
                            <Typography variant="body3" weight="semibold">
                                No {isTestKey ? 'sandbox' : 'live'} API key yet
                            </Typography>
                            <Typography variant="body4" className={styles.empty_desc}>
                                Create a key to start authenticating your{' '}
                                {isTestKey ? 'test' : 'live'} API requests.
                            </Typography>
                            <Button
                                variant="gold"
                                size="small"
                                leftIcon="plus"
                                onClick={() =>
                                    handleCreateApiKey(refetchApiKeys, apiKeys || undefined, env)
                                }>
                                Create key
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export default DevelopersApiKeysLayout;
